import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, Navigate } from 'react-router-dom';
import { CheckCircle, Phone, Building2, MapPin, Clock, Award } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import RelatedCaseStudies from '@/components/RelatedCaseStudies';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cityData } from '@/data/commercialEpcCityData';

// Lightweight Commercial EPC city page. Routed via /commercial-epc-<citySlug>
const CommercialEPCCity = () => {
  const { pathname } = useLocation();
  const citySlug = pathname.replace(/^\/commercial-epc-/, '').replace(/\/$/, '');
  const data = cityData[citySlug];

  if (!data) return <Navigate to="/commercial-epc" replace />;

  const { city, county, countySlug, postcodes, intro, coverage, propertyTypes, travelNote } = data;
  const route = `/commercial-epc-${citySlug}`;
  const canonical = `https://energyperformancedirect.co.uk${route}`;

  const faqs = [
    {
      question: `How much does a Commercial EPC cost in ${city}?`,
      answer: `Level 3 Commercial EPC pricing in ${city} starts at £180 for small offices, shops or units up to 500 m². Medium properties (500–2,000 m²) typically £400–£800. ${travelNote}`,
    },
    {
      question: `Do I need an NDEPC for my ${city} commercial property?`,
      answer: `Yes — under the Energy Performance of Buildings Regulations a valid Non-Domestic EPC (NDEPC) is required when any commercial property in ${city} is sold, let or newly constructed. The certificate is valid for 10 years and is produced by an accredited Non-Domestic Energy Assessor (NDEA) using SBEM software.`,
    },
    {
      question: `What if my ${city} property is rated F or G?`,
      answer: `Since April 2023 it is unlawful to continue to let a commercial property in ${city} rated F or G without a registered MEES exemption. Under DESNZ's June 2026 interim response the 2027 EPC C milestone was dropped; a proposed EPC B by 2031 will apply only to buildings over 1,000 m² (pending secondary legislation), and smaller buildings remain at Band E. We pair every EPC with the option of a separate MEES Commercial Advice Report so ${city} landlords have a costed improvement plan, not just a certificate.`,
    },
  ];

  return (
    <>
      <Helmet>
        <title>{`Commercial EPC ${city} | NDEPC, NDEA, Level 3 | ${county} | Energy Performance Direct`}</title>
        <meta name="description" content={`Accredited NDEA Commercial EPC (NDEPC) assessments in ${city}, ${county} from £180. Level 3 EPCs for ${postcodes}. SBEM calculations and MEES advice. Call 01392 980077.`} />
        <meta name="keywords" content={`Commercial EPC ${city}, NDEPC ${city}, NDEA ${city}, Level 3 EPC ${city}, Non-Domestic Energy Performance Certificate ${city}, SBEM ${city}, MEES commercial ${city}`} />
        <link rel="canonical" href={canonical} />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Service',
            serviceType: 'Commercial Energy Performance Certificate (NDEPC)',
            name: `Commercial EPC ${city}`,
            provider: {
              '@type': 'LocalBusiness',
              name: 'Energy Performance Direct',
              telephone: '+44-1392-980077',
              email: 'info@energyperformancedirect.co.uk',
              priceRange: '£180 - £1500',
            },
            areaServed: { '@type': 'City', name: city, containedInPlace: { '@type': 'AdministrativeArea', name: county } },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((f) => ({
              '@type': 'Question',
              name: f.question,
              acceptedAnswer: { '@type': 'Answer', text: f.answer },
            })),
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <Navbar />

        {/* Hero */}
        <section className="py-16 bg-gradient-to-br from-green-50 via-cyan-50 to-green-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-block mb-4 px-4 py-2 bg-white rounded-full shadow-sm">
                <span className="text-sm font-medium text-green-600">NDEA Accredited · {county} · {postcodes}</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
                Commercial EPC (NDEPC) Assessments in {city}
              </h1>
              <p className="text-lg text-gray-600 mb-6">
                Accredited Non-Domestic Energy Assessors for {city} businesses. Level 3 NDEPCs from £180, plus SBEM calculations for new-build and refurb projects. Linked MEES advice for landlords planning the path to Band C and B.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link to="/booking" data-testid={`${citySlug}-book-btn`}>
                  <Button className="bg-gradient-to-r from-green-600 to-cyan-500 hover:from-green-700 hover:to-cyan-600 text-white px-8 py-6 text-lg rounded-full shadow-lg">
                    Get a Quote in 2 Minutes
                  </Button>
                </Link>
                <a href="tel:01392980077" data-testid={`${citySlug}-call-btn`}>
                  <Button variant="outline" className="border-2 border-green-600 text-green-600 hover:bg-green-50 px-8 py-6 text-lg rounded-full">
                    <Phone className="w-5 h-5 mr-2" />
                    01392 980077
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Row */}
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
              <div className="text-center">
                <Award className="w-10 h-10 text-green-600 mx-auto mb-2" />
                <h3 className="text-base font-bold">NDEA Qualified</h3>
                <p className="text-sm text-gray-600">Elmhurst accredited</p>
              </div>
              <div className="text-center">
                <Clock className="w-10 h-10 text-cyan-500 mx-auto mb-2" />
                <h3 className="text-base font-bold">5–7 Day Turnaround</h3>
                <p className="text-sm text-gray-600">Survey to lodged NDEPC</p>
              </div>
              <div className="text-center">
                <MapPin className="w-10 h-10 text-green-600 mx-auto mb-2" />
                <h3 className="text-base font-bold">{city} Local</h3>
                <p className="text-sm text-gray-600">{postcodes}</p>
              </div>
              <div className="text-center">
                <Building2 className="w-10 h-10 text-cyan-500 mx-auto mb-2" />
                <h3 className="text-base font-bold">From £180</h3>
                <p className="text-sm text-gray-600">Level 3 pricing</p>
              </div>
            </div>
          </div>
        </section>

        {/* Body */}
        <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Commercial EPC Services in {city}</h2>

              <div className="prose prose-lg max-w-none text-gray-700 space-y-5">
                <p>{intro}</p>

                <h3 className="text-2xl font-bold text-gray-900 mt-6 mb-3">{city} commercial property we assess</h3>
                <p>{propertyTypes}</p>

                <h3 className="text-2xl font-bold text-gray-900 mt-6 mb-3">Areas covered around {city}</h3>
                <p>{coverage}</p>

                <h3 className="text-2xl font-bold text-gray-900 mt-6 mb-3">SBEM &amp; new-build EPCs in {city}</h3>
                <p>
                  Alongside the standard Non-Domestic EPC we provide full <Link to="/sbem-explained" className="text-green-600 hover:text-green-700 underline">SBEM calculations</Link> for new-build and major refurbishment projects in {city} that need Part L Building Regulations compliance, plus the as-built commercial EPC for Building Control sign-off.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-6 mb-3">MEES advice for {city} landlords</h3>
                <p>
                  Since April 2023 it is unlawful to let a {city} commercial property rated F or G without a registered exemption. Under DESNZ&rsquo;s June 2026 interim response the 2027 EPC C milestone was dropped; a proposed EPC B by 2031 will apply only to buildings over 1,000 m² (pending secondary legislation), and smaller buildings continue at the current Band E minimum. Our <Link to="/services/commercial_advice" className="text-green-600 hover:text-green-700 underline">Commercial Energy Advice (MEES) consultancy</Link> sits alongside the EPC and models realistic upgrades — LED re-lamping, controls, insulation, heat pump retrofit, solar PV — with cost and payback so {city} landlords have a defensible improvement plan.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-6 mb-3">{city} pricing</h3>
                <p>
                  Level 3 Commercial EPC pricing in {city}: small offices/retail (up to 500 m²) £180–£400, medium warehouses (500–2,000 m²) £400–£800, larger properties priced individually. {travelNote}
                </p>
              </div>

              {/* FAQ */}
              <div className="mt-14">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">{city} Commercial EPC FAQs</h2>
                <div className="space-y-4">
                  {faqs.map((faq, idx) => (
                    <Card key={idx} className="border-none shadow-md">
                      <CardContent className="p-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{faq.question}</h3>
                        <p className="text-gray-700">{faq.answer}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Cross-links */}
              <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4">
                <Link to={`/commercial-epc-${countySlug}`} className="p-5 bg-white border border-gray-200 rounded-lg hover:shadow-lg transition-all">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-gray-900">Commercial EPC {county}</h3>
                      <p className="text-sm text-gray-600">All {county} towns &amp; districts</p>
                    </div>
                  </div>
                </Link>
                <Link to="/commercial-epc" className="p-5 bg-white border border-gray-200 rounded-lg hover:shadow-lg transition-all">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-gray-900">All Commercial EPC Services</h3>
                      <p className="text-sm text-gray-600">NDEPC, SBEM &amp; MEES advice</p>
                    </div>
                  </div>
                </Link>
              </div>

              {/* CTA */}
              <div className="mt-12 bg-gradient-to-r from-green-600 to-cyan-500 rounded-2xl p-8 text-white text-center">
                <h2 className="text-3xl font-bold mb-3">Book Your {city} Commercial EPC</h2>
                <p className="text-lg mb-6">Accredited NDEA assessor. Lodged within 5–7 working days.</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/booking">
                    <Button className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-full">Book Online Now</Button>
                  </Link>
                  <a href="tel:01392980077">
                    <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-green-600 px-8 py-6 text-lg rounded-full">
                      <Phone className="w-5 h-5 mr-2" />Call 01392 980077
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <RelatedCaseStudies filterType="commercial" title={`Commercial buildings near ${city}`} subtitle="Real projects we've assessed and modelled across the South West — see the rating journey and outcome." limit={3} />

        <Footer />
      </div>
    </>
  );
};

export default CommercialEPCCity;
